import React, { useContext, useEffect, useState } from 'react'
import { useMutation } from '@apollo/react-hooks'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { makeStyles } from '@material-ui/core/styles'
import CircularProgress from '@material-ui/core/CircularProgress'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Grid from '@material-ui/core/Grid'
import FormHelperText from '@material-ui/core/FormHelperText'
import IconButton from '@material-ui/core/IconButton'
import DeleteIcon from '@material-ui/icons/Delete'
import CloseIcon from '@material-ui/icons/Close'
import Alert from '@material-ui/lab/Alert'

import PagErrorBoundary from '@/components/pag-error-boundary'
import PagAutocomplete from '@/components/pag-form/pag-autocomplete'
import PagNumberFormatInput from '@/components/pag-form/pag-number-format-Input'
import PagTextInput from '@/components/pag-form/pag-text-input'
import { LANGUAGES } from '@/shared/constants/language'
import { errorHandler } from '@/shared/error-handler'
import {
  ADD_DELIVERABLE,
  UPDATE_DELIVERABLE,
} from '@/shared/graphql/mutations/mutationDeliverables'
import { useAuth0 } from '@/auth/index'
import useMounted from '@/components/hooks/use-mounted'
import { Context } from '@/shared/store/reducers/global-reducer'
import { callPagApi } from '@/shared/services/pag-api'
import { IMediaType } from '@/shared/models/media-type'
import { IMedium } from '@/shared/models/medium'
import { ISpotType } from '@/shared/models/spot-type'
import { stringToNumber } from '@/utils/numeric'

const FormSchema = yup.object().shape({
  spotName: yup.string().required('Spot Name is required.'),
  spotId: yup.string().required('Spot Identifier is required.'),
})

const FIELD_ERROR_MESSAGE = 'This field could not be displayed. Please close and reopen the dialog.'
const DIALOG_ERROR_MESSAGE =
  'Something went wrong while displaying this form. Please close and reopen the dialog.'

interface ILanguageOption {
  label: string
  value: string
}

const safeLabel = (label: unknown): string => (label == null ? '' : String(label))

const buildLanguageOptions = (language: unknown): ILanguageOption[] => {
  const options = LANGUAGES.map((l) => ({ ...l }))
  // Keep the stored value as-is so it matches the form value; trim only to reject blanks.
  if (typeof language === 'string' && language.trim()) {
    if (!options.some((o) => o.value === language)) {
      options.push({ label: language, value: language })
    }
  }
  return options
}

const FieldBoundary = ({ name, children }: { name: string; children: React.ReactNode }) => (
  <PagErrorBoundary
    name={name}
    fallback={<FormHelperText error>{FIELD_ERROR_MESSAGE}</FormHelperText>}
  >
    {children}
  </PagErrorBoundary>
)

const useStyles = makeStyles((theme) => ({
  root: {},
  dialogTitle: {
    borderBottom: '1px solid',
    padding: 12,
  },
  dialogContent: {
    padding: '24px 16px 16px',
  },
  dialogAction: {
    padding: 16,
  },
  closeButton: {
    fontSize: 18,
    position: 'absolute',
    right: 2,
    top: 2,
    color: 'black',
  },
}))

const DeliverableEditDialog = (props: any) => {
  const {
    open,
    deliverable,
    projectId,
    mediaTypes,
    mediums,
    spotTypes,
    handleClose,
    handleDelete,
    handleOpenAdIdSearchDialog,
  } = props

  const classes = useStyles()
  const { getTokenSilently } = useAuth0()
  const { store } = useContext(Context)
  const { activeTenant } = store
  const mountedRef = useMounted(true)
  const [addDeliverable, addDeliverableRes] = useMutation(ADD_DELIVERABLE, {
    onError: (err) => {
      errorHandler(err)
    },
    onCompleted: (res) => {
      const temp = res?.addDeliverable
      if (temp) {
        handleClose(temp)
      }
    },
  })
  const [updateDeliverable, updateDeliverableRes] = useMutation(UPDATE_DELIVERABLE, {
    onError: (err) => {
      errorHandler(err)
    },
    onCompleted: (res) => {
      const temp = res?.updateDeliverable
      if (temp) {
        handleClose(temp)
      }
    },
  })

  const hookFormMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(FormSchema),
  })

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
  } = hookFormMethods

  const [languageOptions, setLanguageOptions] = useState<ILanguageOption[]>(() =>
    buildLanguageOptions(deliverable?.language)
  )

  useEffect(() => {
    // Keep the edited deliverable's language selectable if the dialog stays mounted across deliverables.
    const extra = buildLanguageOptions(deliverable?.language)
    setLanguageOptions((prev) => {
      const missing = extra.filter((o) => !prev.some((p) => p.value === o.value))
      return missing.length ? [...prev, ...missing] : prev
    })
  }, [deliverable])

  const handleSubmitForm = (formData: any) => {
    if (!activeTenant?.id) return
    const abortController = new AbortController()
    try {
      const callApi = async (data: any) => {
        try {
          await callPagApi(
            '',
            getTokenSilently,
            payload?.id ? updateDeliverable : addDeliverable,
            data,
            abortController.signal
          )
        } catch (e) {}
      }

      const payload: any = {
        tenantId: activeTenant.id,
        id: deliverable?.id,
        data: {
          language: formData.language,
          length: stringToNumber(formData.length),
          spotName: formData.spotName,
          spotId: formData.spotId,
          mediaTypeId: formData.mediaTypeId,
          mediumId: formData.mediumId,
          spotTypeId: formData.spotTypeId,
          projectId,
        },
      }

      callApi(payload)
    } catch (err) {
      if (abortController) abortController.abort()
    }
  }

  return (
    <Dialog
      className={classes.root}
      fullWidth={true}
      maxWidth="md"
      open={open}
      onClose={() => handleClose(null)}
      aria-labelledby="deliverable-edit-dialog-title"
    >
      <DialogTitle id="deliverable-edit-dialog-title" className={classes.dialogTitle}>
        {Boolean(deliverable) ? 'Edit Deliverable' : 'Add New Deliverable'}
        <IconButton
          size="small"
          aria-label="close"
          className={classes.closeButton}
          onClick={() => handleClose(null)}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <PagErrorBoundary
          name="DeliverableEditDialog"
          resetKeys={[open, deliverable]}
          fallback={<Alert severity="error">{DIALOG_ERROR_MESSAGE}</Alert>}
        >
          <form
            name="DeliverableForm"
            onSubmit={handleSubmit(handleSubmitForm)}
            // onMouseEnter={(e) => e.stopPropagation()}
            // onMouseLeave={(e) => handleMouseLeave(e)}
          >
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FieldBoundary name="spotId">
                  <Controller
                    render={({ field, fieldState, formState }) => (
                      <PagTextInput
                        label="Spot Identifier"
                        placeholder="Spot Identifier"
                        size="small"
                        variant="outlined"
                        error={fieldState.error}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    )}
                    control={control}
                    name={`spotId`}
                    defaultValue={deliverable?.spotId || ''}
                  />
                </FieldBoundary>
              </Grid>
              <Grid item xs={6}>
                <FieldBoundary name="spotName">
                  <Controller
                    render={({ field, fieldState, formState }) => (
                      <PagTextInput
                        label="Spot Name"
                        placeholder="Spot Name"
                        size="small"
                        variant="outlined"
                        error={fieldState.error}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    )}
                    control={control}
                    name={`spotName`}
                    defaultValue={deliverable?.spotName || ''}
                  />
                </FieldBoundary>
              </Grid>
              <Grid item xs={4}>
                <FieldBoundary name="spotTypeId">
                  <Controller
                    render={({ field, fieldState, formState }) => (
                      <PagAutocomplete
                        {...{
                          optionLabelKey: 'name',
                          optionValueKey: 'id',
                          options: spotTypes,
                          error: fieldState.error,
                          value: field.value,
                          isCreatable: false,
                          AutocompleteProps: {
                            getOptionLabel: (option: ISpotType) => safeLabel(option?.name),
                            getOptionSelected: (option: ISpotType, value: ISpotType | null) =>
                              option.id === value?.id,
                            size: 'small',
                            disableClearable: true,
                            onChange: (event: object, values: ISpotType, reason: string) => {
                              field.onChange(values ? values.id : null)
                            },
                            onBlur: field.onBlur,
                          },
                          InputProps: {
                            variant: 'outlined',
                            label: 'Spot Type',
                            placeholder: 'Spot Type',
                          },
                        }}
                      />
                    )}
                    control={control}
                    name={`spotTypeId`}
                    defaultValue={deliverable?.spotType?.id || null}
                  />
                </FieldBoundary>
              </Grid>
              <Grid item xs={4}>
                <FieldBoundary name="mediaTypeId">
                  <Controller
                    render={({ field, fieldState, formState }) => (
                      <PagAutocomplete
                        {...{
                          name: 'mediaTypeId',
                          optionLabelKey: 'name',
                          optionValueKey: 'id',
                          options: mediaTypes,
                          error: fieldState.error,
                          value: field.value,
                          isCreatable: false,
                          AutocompleteProps: {
                            getOptionLabel: (option: IMediaType) => safeLabel(option?.name),
                            getOptionSelected: (option: IMediaType, value: IMediaType | null) =>
                              option.id === value?.id,
                            size: 'small',
                            disableClearable: true,
                            onChange: (event: object, values: IMediaType, reason: string) => {
                              field.onChange(values ? values.id : null)
                            },
                            onBlur: field.onBlur,
                          },
                          InputProps: {
                            variant: 'outlined',
                            label: 'Media Type',
                            placeholder: 'Media Type',
                          },
                        }}
                      />
                    )}
                    control={control}
                    name={`mediaTypeId`}
                    defaultValue={deliverable?.mediaType?.id || null}
                  />
                </FieldBoundary>
              </Grid>
              <Grid item xs={4}>
                <FieldBoundary name="mediumId">
                  <Controller
                    render={({ field, fieldState, formState }) => (
                      <PagAutocomplete
                        {...{
                          name: 'mediumId',
                          optionLabelKey: 'name',
                          optionValueKey: 'id',
                          options: mediums,
                          error: fieldState.error,
                          value: field.value,
                          isCreatable: false,
                          AutocompleteProps: {
                            getOptionLabel: (option: IMedium) => safeLabel(option?.name),
                            size: 'small',
                            disableClearable: true,
                            onChange: (event: object, values: IMedium, reason: string) => {
                              field.onChange(values ? values.id : null)
                            },
                            onBlur: field.onBlur,
                          },
                          InputProps: {
                            variant: 'outlined',
                            label: 'Medium',
                            placeholder: 'Medium',
                          },
                        }}
                      />
                    )}
                    control={control}
                    name={`mediumId`}
                    defaultValue={deliverable?.medium?.id || null}
                  />
                </FieldBoundary>
              </Grid>
              <Grid item xs={4}>
                <FieldBoundary name="language">
                  <Controller
                    render={({ field, fieldState, formState }) => (
                      <PagAutocomplete
                        {...{
                          name: 'language',
                          optionLabelKey: 'label',
                          optionValueKey: 'value',
                          options: languageOptions,
                          error: fieldState.error,
                          value: field.value,
                          isCreatable: true,
                          AutocompleteProps: {
                            getOptionLabel: (option: ILanguageOption) => safeLabel(option?.label),
                            getOptionSelected: (
                              option: ILanguageOption,
                              value: ILanguageOption | null
                            ) => option?.value === value?.value,
                            size: 'small',
                            disableClearable: true,
                            onChange: (event: object, values: any, reason: string) => {
                              if (!values) {
                                field.onChange(null)
                                return
                              }
                              if (values.isNew) {
                                const newValue = safeLabel(values.newValue).trim()
                                if (!newValue) return
                                const existing = languageOptions.find(
                                  (o) =>
                                    safeLabel(o.label).trim().toLowerCase() ===
                                      newValue.toLowerCase() ||
                                    safeLabel(o.value).trim().toLowerCase() ===
                                      newValue.toLowerCase()
                                )
                                if (existing) {
                                  field.onChange(existing.value)
                                  return
                                }
                                setLanguageOptions((prev) =>
                                  prev.some((o) => o.value === newValue)
                                    ? prev
                                    : [...prev, { label: newValue, value: newValue }]
                                )
                                field.onChange(newValue)
                                return
                              }
                              field.onChange(values.value)
                            },
                            onBlur: field.onBlur,
                          },
                          InputProps: {
                            variant: 'outlined',
                            label: 'Language',
                            placeholder: 'Language',
                          },
                        }}
                      />
                    )}
                    control={control}
                    name={`language`}
                    defaultValue={deliverable?.language || null}
                  />
                </FieldBoundary>
              </Grid>
              <Grid item xs={4}>
                <FieldBoundary name="length">
                  <Controller
                    render={({ field, fieldState }) => (
                      <PagTextInput
                        label="Length"
                        placeholder="Length"
                        size="small"
                        variant="outlined"
                        InputProps={{
                          name: 'length',
                          inputComponent: PagNumberFormatInput,
                          inputProps: {
                            thousandSeparator: true,
                            isNumericString: true,
                            decimalScale: 0,
                          },
                        }}
                        error={fieldState.error}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    )}
                    control={control}
                    name={`length`}
                    defaultValue={deliverable?.length || ''}
                  />
                </FieldBoundary>
              </Grid>
              <Grid item xs={4}>
                {deliverable?.linkTo || ''}
              </Grid>

              <Grid item xs={12} className="pag-text-right">
                {Boolean(deliverable) && (
                  <Button
                    variant="text"
                    color="secondary"
                    startIcon={<DeleteIcon />}
                    style={{ float: 'left', marginRight: 12 }}
                    onClick={() => handleDelete(deliverable)}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  className="pag-button"
                  style={{ float: 'left' }}
                  onClick={() => handleOpenAdIdSearchDialog(true)}
                >
                  Find Ad-ID
                </Button>
                <Button variant="text" color="inherit" onClick={() => handleClose(null)}>
                  Cancel
                </Button>
                &nbsp;&nbsp;&nbsp;
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className="pag-button"
                  disabled={!isValid || addDeliverableRes.loading || updateDeliverableRes.loading}
                >
                  {addDeliverableRes.loading || updateDeliverableRes.loading ? (
                    <CircularProgress size={14} color="secondary" />
                  ) : (
                    'Save'
                  )}
                </Button>
              </Grid>
            </Grid>
          </form>
        </PagErrorBoundary>
      </DialogContent>
    </Dialog>
  )
}

export default DeliverableEditDialog
